import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  MenuItem,
} from "@mui/material";
import {
  useCategories,
  fetchCategoryL1,
  fetchCategoryL2,
  fetchCategoryL3,
  fetchCategoryL4,
} from "../../../lib/api_routes";
import { generateMarketInsights } from "../../../utils/GenerateInsightFormHelpers";
import { CountryDropDown } from "../../ReusableComponents/CountriesDropDown";
import { useTaxonomy } from "../../../contexts/TaxonomyContext";
import { CustomButton } from "../../ReusableComponents/CustomButton";
import { useAccountInfo } from "../../../hooks/useAccountInfo";

export function GenerateInsightsForm({
  isFormOpen,
  setIsFormOpen,
  isLoading,
  setIsLoading,
  isLoaded,
  setIsLoaded,
  loadedCount,
  setLoadedCount,
  totalToLoad,
  setTotalToLoad,
  isGenerating,
  setIsGenerating,
  history,
  setHistory,
  currentEntryId,
  setCurrentEntryId,
  setNewsAffectingPlans,
  setSizeOfMarket,
  setKeyChallengesAndTrends,
  setPorterFiveForces,
  setGeneralNews,
  setCategorySpecificNews,
  setCostDrivers,
  setSuppliers,
  setAccreditations,
  setRegulations,
  setSuppliersToShow,
  setExistingSuppliers,
  setGlobalSuppliers,
  setFinancialReports,
  formValues,
  setFormValues,
  setTaxonomyPath,
  trackAction,
  resetSession,
  setSpendOverview,
  setSpendBySupplier,
  setSpendByBusinessUnit,
  setSpendByDepartment,
  setSpendL3Insights,
  setSpendProcurementLevers,
  setSpendCostDrivers,
  setSpendInflationOverview,
  setSpendCommodityInflation,
}) {
  // Account Info from hooks
  const { accountInfo } = useAccountInfo();

  const { setTaxonomyPath: setTaxonomyPathContext } = useTaxonomy();

  const [categoryL1Options, setCategoryL1Options] = useState([]);
  const [categoryL2Options, setCategoryL2Options] = useState([]);
  const [categoryL3Options, setCategoryL3Options] = useState([]);
  const [categoryL4Options, setCategoryL4Options] = useState([]);
  const [errors, setErrors] = useState({});

  // Async when accountInfo loads
  useEffect(() => {
    if (accountInfo?.sector && !formValues.sector) {
      setFormValues((prev) => ({ ...prev, sector: accountInfo.sector }));
    }
  }, [accountInfo?.sector]);

  // Taxonomy paths
  useEffect(() => {
    if (!accountInfo?.accountId) return;

    async function getCategoryL1() {
      const result = await fetchCategoryL1(accountInfo?.accountId);
      if (Array.isArray(result) && result.length > 0) {
        setCategoryL1Options(result);
      }
    }
    getCategoryL1();
  }, [accountInfo?.accountId]);

  // Fetch L2 and L3 categories based on selected category
  useEffect(() => {
    if (!formValues.category) {
      setCategoryL2Options([]);
      setCategoryL3Options([]);
      return;
    }
    fetchCategoryL2(formValues.category, accountInfo?.accountId)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setCategoryL2Options(
            data.map((item) => ({ value: item, label: item })),
          );
        } else {
          setCategoryL2Options([]);
        }
        setCategoryL3Options([]);
      })
      .catch((error) => {
        console.error("Error fetching L2 categories:", error);
        setCategoryL2Options([]);
        setCategoryL3Options([]);
      });
  }, [formValues.category, accountInfo?.accountId]);

  // Fetch L3 categories based on selected L2 category
  useEffect(() => {
    if (!formValues.L2) {
      setCategoryL3Options([]);
      return;
    }

    fetchCategoryL3(formValues, accountInfo?.accountId)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setCategoryL3Options(
            data.map((item) => ({ value: item, label: item })),
          );
        } else {
          setCategoryL3Options([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching L3 categories:", error);
        setCategoryL3Options([]);
      });
  }, [formValues.L2, accountInfo?.accountId]);

  // Fetch L4 categories based on selected L3 category
  useEffect(() => {
    if (!formValues.L3 || !accountInfo) {
      setCategoryL4Options([]);
      return;
    }

    fetchCategoryL4(formValues, accountInfo?.accountId)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setCategoryL4Options(
            data.map((item) => ({ value: item, label: item })),
          );
        } else {
          setCategoryL4Options([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching L4 categories:", error);
        setCategoryL4Options([]);
      });
  }, [formValues.L3, accountInfo, accountInfo?.accountId]);

  // Validate all form fields
  const isFormValid = () => {
    const newErrors = {};
    const fieldsToValidate = [
      "sector",
      "category",
      "L2",
      "L3",
      "country",
      "context",
    ];

    fieldsToValidate.forEach((field) => {
      const errorMsg = validateField(field, formValues[field]);
      if (errorMsg) {
        newErrors[field] = errorMsg;
      }
    });

    setErrors(newErrors);

    const hasErrors = Object.keys(newErrors).length > 0;
    return !hasErrors;
  };

  // Checks if a field has valid value and return an error message if invalid
  const validateField = (field, value) => {
    const fieldToLabel = {
      sector: "Sector",
      category: "Category",
      L2: "Category L2",
      L3: "Category L3",
      country: "Country",
      context: "Context",
    };

    // Context is optional, but must not exceed 255 characters when populated
    if (field === "context") {
      if (value && value.length > 255) {
        return `${fieldToLabel[field]} must not exceed 255 characters`;
      }
      return;
    }

    // If this is the sector field, accept the account-level sector as valid
    if (field === "sector" && accountInfo?.sector) {
      return;
    }

    if (value && value.trim().length > 0) {
      return;
    } else if (field === "L3" && !accountInfo?.haveL4) {
      return;
    } else {
      return `${fieldToLabel[field]} must be filled`;
    }
  };

  // Helper function to update a single form field
  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));

    const errorMsg = validateField(field, value);
    setErrors({ ...errors, [field]: errorMsg });
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  const handleRefresh = () => {
    setFormValues({
      category: "",
      L2: "",
      L3: "",
      L4: "",
      sector: accountInfo?.sector ?? "",
      country: "",
      approximatespend: 0,
      context: "",
    });
    setErrors({});
  };

  // Check if any form field has a non-empty, non-zero value
  const hasFormData = Object.entries(formValues).some(([field, value]) => {
    // Skip sector if it's pre-filled from account
    if (field === "sector" && accountInfo?.sector != null) {
      return false;
    }
    return value && value !== "" && value !== 0;
  });

  const incrementLoadedCount = () => setLoadedCount((prev) => prev + 1);

  // Supplier format helpers
  function formatSuppliersData(data) {
    if (!data) return [];
    return data.map((row) => ({
      "Supplier Name": row["Supplier"] || "",
      "National Status": row["National_Status"] || "",
      "Service Type": row["Service_Type"] || "",
      Website: row["Website"] || "",
      Accreditations: row["Accreditations"] || "",
      Turnover: row["Turnover"] || "",
      Description: row["Description"] || "",
    }));
  }

  function formatExistingSuppliersData(data) {
    if (!data) return [];
    return data.map((row) => ({
      "Supplier Name": row["Normalised_Supplier_Names"] || "",
      Description: row.Description || "",
      "Category L1": row.L1 || "",
      "Category L2": row.L2 || "",
      "Category L3": row.L3 || "",
      "Category L4": row.L4 || "",
    }));
  }

  // Wrapper to set taxonomy path in both context and parent if provided
  const handleSetTaxonomyPath = (path) => {
    setTaxonomyPathContext(path);
    if (typeof setTaxonomyPath === "function") {
      setTaxonomyPath(path);
    }
  };

  const generateMarketInsightsArgs = {
    values: formValues,
    accountInfo,
    isValid: isFormValid,
    setTaxonomyPath: handleSetTaxonomyPath,
    setIsLoaded,
    setIsLoading,
    setLoadedCount,
    setIsGenerating,
    setHistory,
    setCurrentEntryId,
    closeForm,
    setTotalToLoad,
    incrementLoadedCount,
    setCostDrivers,
    setNewsAffectingPlans,
    setSizeOfMarket,
    setKeyChallengesAndTrends,
    setPorterFiveForces,
    setGeneralNews,
    setCategorySpecificNews,
    setFinancialReports,
    setSuppliers,
    setAccreditations,
    setRegulations,
    setSuppliersToShow,
    setExistingSuppliers,
    setGlobalSuppliers,
    formatSuppliersData,
    formatExistingSuppliersData,
    trackAction,
    resetSession,
    onComplete: () => {
      setIsLoading(false);
      setIsLoaded(true);
      setIsGenerating(false);
    },
    setSpendOverview,
    setSpendBySupplier,
    setSpendByBusinessUnit,
    setSpendByDepartment,
    setSpendL3Insights,
    setSpendProcurementLevers,
    setSpendCostDrivers,
    setSpendInflationOverview,
    setSpendCommodityInflation,
  };

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        generateMarketInsights(generateMarketInsightsArgs);
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        px: { xs: 1, sm: 2 },
        minWidth: 400,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="h4"
        sx={{ mb: 4, textAlign: "center", fontWeight: 500 }}
      >
        Generate Insights
      </Typography>
      <TextField
        required
        id="sector-input"
        label="Sector"
        placeholder="Please enter a sector"
        fullWidth
        size="small"
        value={formValues.sector || accountInfo?.sector || ""}
        onChange={(e) => handleChange("sector", e.target.value)}
        error={!!errors.sector}
        helperText={errors.sector}
        disabled={!accountInfo?.editableSector}
        sx={{ mb: 0.5 }}
      />
      <FormControl
        fullWidth
        required
        error={!!errors.category}
        size="small"
        sx={{ mb: 0.5 }}
      >
        <InputLabel id="category-select-label">Category</InputLabel>
        <Select
          labelId="category-select-label"
          id="category-select"
          label="Category"
          placeholder="Please select a category"
          value={formValues.category || ""}
          disabled={accountInfo?.category != null}
          onChange={(e) => handleChange("category", e.target.value)}
        >
          {categoryL1Options.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </Select>
        {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
      </FormControl>
      <FormControl
        fullWidth
        required
        error={!!errors.L2}
        size="small"
        disabled={!formValues.category}
        sx={{ mb: 0.5 }}
      >
        <InputLabel id="categoryL2-select-label">Category L2</InputLabel>
        <Select
          labelId="categoryL2-select-label"
          id="categoryL2-select"
          label="Category L2"
          placeholder="Please select a category L2"
          value={formValues.L2 || ""}
          onChange={(e) => handleChange("L2", e.target.value)}
        >
          {categoryL2Options.map((category) => (
            <MenuItem key={category.value} value={category.value}>
              {category.label}
            </MenuItem>
          ))}
        </Select>
        {errors.L2 && <FormHelperText>{errors.L2}</FormHelperText>}
      </FormControl>
      <FormControl
        fullWidth
        required={accountInfo?.haveL4}
        error={!!errors.L3}
        size="small"
        disabled={!formValues.L2}
        sx={{ mb: 0.5 }}
      >
        <InputLabel id="categoryL3-select-label">Category L3</InputLabel>
        <Select
          labelId="categoryL3-select-label"
          id="categoryL3-select"
          label="Category L3"
          placeholder="Please select a category L3"
          value={formValues.L3 || ""}
          onChange={(e) => handleChange("L3", e.target.value)}
        >
          {categoryL3Options.map((category) => (
            <MenuItem key={category.value} value={category.value}>
              {category.label}
            </MenuItem>
          ))}
        </Select>
        {errors.L3 && <FormHelperText>{errors.L3}</FormHelperText>}
      </FormControl>
      {accountInfo?.haveL4 && (
        <FormControl
          fullWidth
          size="small"
          disabled={!formValues.L3}
          sx={{ mb: 0.5 }}
        >
          <InputLabel id="categoryL4-select-label">Category L4</InputLabel>
          <Select
            labelId="categoryL4-select-label"
            id="categoryL4-select"
            label="Category L4"
            placeholder="Please select a category L4"
            value={formValues.L4 || ""}
            onChange={(e) => handleChange("L4", e.target.value)}
          >
            {categoryL4Options.map((category) => (
              <MenuItem key={category.value} value={category.value}>
                {category.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <CountryDropDown
        value={formValues.country}
        onChange={(newValue) => handleChange("country", newValue)}
        error={!!errors.country}
        helperText={errors.country}
        required
      />
      <TextField
        id="context-input"
        label="Context"
        fullWidth
        size="small"
        value={formValues.context || ""}
        onChange={(e) => handleChange("context", e.target.value)}
        inputProps={{ maxLength: 255 }}
        error={!!errors.context}
        helperText={
          errors.context || `${(formValues.context || "").length}/255`
        }
        sx={{ mb: 0.5 }}
      />
      <Typography
        variant="body1"
        sx={{ textAlign: "center", mt: 1.5, color: "text.secondary" }}
      >
        Press below to generate the insights...
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          width: "100%",
          mt: 1.5,
          mb: 2,
          gap: { xs: 1, sm: 2 },
        }}
      >
        <CustomButton
          text="Generate Insights"
          color="primary.brandSecondary"
          backgroundColor="primary.main"
          borderRadius="6px"
          fullWidth
          onClick={() => generateMarketInsights(generateMarketInsightsArgs)}
        />
        <CustomButton
          text="Clear Form"
          color="primary.brandSecondary"
          backgroundColor="primary.main"
          borderRadius="6px"
          fullWidth
          onClick={handleRefresh}
          disabled={!hasFormData}
        />
      </Box>
    </Box>
  );
}
